import React from 'react';
import { Alert, App, Button, Card, DatePicker, Form, Radio, Space, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import hrmsService, { hrmsKeys, PAYROLL_RUN_SCOPE_LABEL } from '@/services/hrms.service';
import { PAYROLL_STATUS_COLOR } from './shared/hrmsStatus';

const PayrollPeriodFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const period = Form.useWatch('period', form) as dayjs.Dayjs | undefined;

  const { data: periods = [] } = useQuery({
    queryKey: hrmsKeys.payrollPeriods(),
    queryFn: () => hrmsService.listPayrollPeriods(),
  });
  const runsThisMonth = period
    ? periods.filter((p: any) => p.period_year === period.year() && p.period_month === period.month() + 1)
    : [];

  const save = async () => {
    try {
      const v = await form.validateFields();
      const d = v.period as dayjs.Dayjs;
      const created = await hrmsService.openPayrollPeriod({
        period_year: d.year(),
        period_month: d.month() + 1,
        department_id: v.department_id,
        run_scope: v.run_scope,
      });
      message.success(`Payroll run ${created?.period_number || ''} opened`);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.payrollPeriods() });
      navigate(`/hr/payroll/periods/${created?.id || ''}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Open failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Open payroll run"
          subtitle="A month can have several runs; each employee is paid in only one run per month"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/payroll')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>
                Open run
              </Button>
            </Space>
          }
        />
      }
    >
      <Form form={form} layout="vertical" initialValues={{ period: dayjs(), run_scope: 'approved_timesheets' }}>
        <Card title="Run">
          <Form.Item name="period" label="Month" rules={[{ required: true }]}>
            <DatePicker picker="month" style={{ width: 280 }} />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect style={{ width: 280 }} />
          </Form.Item>
          <Form.Item name="run_scope" label="Employees in this run" rules={[{ required: true }]}>
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="approved_timesheets">
                  {PAYROLL_RUN_SCOPE_LABEL.approved_timesheets}
                  <Typography.Text type="secondary"> — employees whose monthly timesheet is approved</Typography.Text>
                </Radio>
                <Radio value="all">
                  {PAYROLL_RUN_SCOPE_LABEL.all}
                  <Typography.Text type="secondary"> — absences only deducted where a timesheet is approved</Typography.Text>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Typography.Text type="secondary">
            Employees already included in another run of the same month are skipped automatically.
          </Typography.Text>
        </Card>
        {runsThisMonth.length > 0 && (
          <Card title="Existing runs for this month" style={{ marginTop: 16 }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Opening another run pays only the employees not yet in these runs."
            />
            <Space direction="vertical">
              {runsThisMonth.map((p: any) => (
                <Space key={p.id}>
                  <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/hr/payroll/periods/${p.id}`)}>
                    {p.period_number}
                  </Button>
                  <Tag color={PAYROLL_STATUS_COLOR[p.status] || 'default'}>{p.status}</Tag>
                  <Typography.Text type="secondary">
                    {PAYROLL_RUN_SCOPE_LABEL[p.run_scope as keyof typeof PAYROLL_RUN_SCOPE_LABEL] || p.run_scope} ·{' '}
                    {p.employee_count ?? 0} employee(s)
                  </Typography.Text>
                </Space>
              ))}
            </Space>
          </Card>
        )}
      </Form>
    </WorkspaceLayout>
  );
};

export default PayrollPeriodFormPage;
